"use client";

import React, { useMemo, useCallback } from "react";
import type { ProductVariation } from "../../utils/productDetail";

export interface VariationSelectorProps {
  variations: ProductVariation[];
  selectedAttributes: Record<string, string>;
  onSelect: (attributes: Record<string, string>) => void;
  errors?: Record<string, string | null | undefined>;
}

const VariationSelector: React.FC<VariationSelectorProps> = ({
  variations,
  selectedAttributes,
  onSelect,
  errors = {},
}) => {
  const attributeMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    variations.forEach((variation) => {
      Object.entries(variation.attributes).forEach(([key, value]) => {
        if (!map.has(key)) {
          map.set(key, new Set());
        }
        if (value) {
          map.get(key)!.add(value);
        }
      });
    });
    return map;
  }, [variations]);

  const getAvailableValues = useCallback(
    (attributes: Record<string, string>, attributeKey: string) => {
      const available = new Set<string>();
      variations.forEach((variation) => {
        let matches = true;
        for (const [key, value] of Object.entries(attributes)) {
          if (key === attributeKey) {
            continue;
          }
          if (value && variation.attributes[key] !== value) {
            matches = false;
            break;
          }
        }
        if (matches) {
          const candidate = variation.attributes[attributeKey];
          if (candidate) {
            available.add(candidate);
          }
        }
      });
      return available;
    },
    [variations]
  );

  const handleSelect = useCallback(
    (attributeKey: string, value: string) => {
      const nextAttributes: Record<string, string> = {
        ...selectedAttributes,
        [attributeKey]: value,
      };

      const matchingVariations = variations.filter((variation) =>
        Object.entries(nextAttributes).every(
          ([key, attrValue]) => variation.attributes[key] === attrValue
        )
      );

      if (matchingVariations.length === 0) {
        attributeMap.forEach((_values, key) => {
          if (key === attributeKey) {
            return;
          }
          const available = getAvailableValues(nextAttributes, key);
          if (!available.has(nextAttributes[key])) {
            const [firstValue] = Array.from(available);
            if (firstValue) {
              nextAttributes[key] = firstValue;
            } else {
              delete nextAttributes[key];
            }
          }
        });
      }

      onSelect(nextAttributes);
    },
    [attributeMap, getAvailableValues, onSelect, selectedAttributes, variations]
  );

  if (attributeMap.size === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {Array.from(attributeMap.entries()).map(([attributeKey, values]) => {
        const availableValues = getAvailableValues(
          selectedAttributes,
          attributeKey
        );
        const error = errors[attributeKey];
        return (
          <div key={attributeKey} className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-gray-900">
                {attributeKey.charAt(0).toUpperCase() + attributeKey.slice(1)}
              </span>
              {error ? (
                <span className="text-xs font-medium text-red-500">
                  {error}
                </span>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.from(values).map((value) => {
                const isSelected = selectedAttributes[attributeKey] === value;
                const isAvailable = availableValues.has(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleSelect(attributeKey, value)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      isSelected
                        ? "border-gray-900 bg-gray-900 text-white"
                        : "border-default-200 bg-white text-gray-900 hover:border-gray-900 hover:text-gray-900"
                    } ${isAvailable ? "" : "opacity-40 pointer-events-none"}`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VariationSelector;
